import type {
  ActionDescription,
  Application,
  InputDescription,
  SelectEditorOption,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import {
  INPUT_APPLICATION_ID,
  INPUT_SOLUTION_ID,
} from "../../../../utils/constants";
import { hasVisibleEditor } from "../../../../utils/inputs";
import type { SolutionModel } from "../../../../types/solution";
import type { InputCandidate } from "../../../../types/candidate";

const buildInitialInput = (inputId: string, value: string): InputCandidate => ({
  inputId,
  valueDescription: {
    type: ValueDescription_T.STRING,
    multiple: false,
  },
  value: {
    values: [
      {
        value: {
          oneofKind: "string",
          string: value,
        },
      },
    ],
  },
});

/**
 * The values a freshly added action starts with. Only inputs the action's own
 * description declares with a visible editor are seeded: an action whose table
 * comes from its record list declares them hidden, and pre-filling those would
 * put back a choice the editor no longer makes.
 */
export const buildInitialInputs = (
  description: ActionDescription,
  values: Record<string, string | undefined>,
): InputCandidate[] =>
  description.inputs.filter(hasVisibleEditor).flatMap((input) => {
    const value = values[input.inputId];
    return value === undefined || value === ""
      ? []
      : [buildInitialInput(input.inputId, value)];
  });

const withOptions = (
  input: InputDescription,
  options: SelectEditorOption[],
): InputDescription => {
  const editor = input.editor;
  if (
    editor?.editor.oneofKind !== "select" ||
    editor.editor.select.options.length > 0
  ) {
    return input;
  }
  return {
    ...input,
    editor: {
      ...editor,
      editor: {
        oneofKind: "select",
        select: { ...editor.editor.select, options },
      },
    },
  };
};

/**
 * Integrations register the Solution and App pickers with no options, since
 * only the dynamic description -- listed through the automation's credential --
 * can fill them. Until that arrives the editor's own lists stand in, so a
 * seeded value has something to render against and the first tables a member
 * is offered are the ones their permissions already reach.
 */
export const withStoreSelectOptions = (
  description: ActionDescription,
  solutions: SolutionModel[],
  applications: Application[],
): ActionDescription => ({
  ...description,
  inputs: description.inputs.map((input) => {
    switch (input.inputId) {
      case INPUT_SOLUTION_ID:
        return withOptions(
          input,
          solutions.map((solution) => ({
            value: solution.id,
            label: solution.name,
            color: "",
          })),
        );
      case INPUT_APPLICATION_ID:
        return withOptions(
          input,
          applications.map((application) => ({
            value: application.id,
            label: application.label,
            color: "",
          })),
        );
      default:
        return input;
    }
  }),
});
